import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmText: string;
  cancelText: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/** Small confirmation window for actions that change many things at once. Esc and the backdrop cancel. */
export function ConfirmDialog({ open, title, children, confirmText, cancelText, busy = false, onConfirm, onClose }: Props) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-canvas/80" onClick={() => { if (!busy) onClose(); }} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby="confirm-title" className="relative w-full max-w-md rounded-2xl border border-line bg-panel p-6 shadow-2xl">
        <h2 id="confirm-title" className="text-lg font-semibold text-ink">{title}</h2>
        <div className="mt-3 space-y-3 text-sm text-ink-muted">{children}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button ref={cancelRef} type="button" disabled={busy} onClick={onClose}
            className="rounded-full border border-line px-4 py-2 text-sm font-medium text-ink hover:bg-raised disabled:opacity-50">
            {cancelText}
          </button>
          <button type="button" disabled={busy} onClick={onConfirm}
            className="rounded-full bg-accent-600 px-5 py-2 text-sm font-semibold text-white hover:bg-accent-500 disabled:opacity-50">
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
